import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot, CanActivate, CanActivateChild, CanDeactivate, Router, RouterStateSnapshot, UrlTree
} from '@angular/router';
import {IdleLockService} from '../_services/idle-lock.service';
import {UNLOCK_ROUTE, unlockNavigationFor} from '../auth/models/web-session.util';

/**
 * JS-1396: while this device is locked (browser reopened, or no heartbeat
 * past the grace), no app route activates - Back / forward or a link from
 * the unlock screen is turned into the unlock screen before the target's
 * components (and their requests) exist. IdleLockService's NavigationEnd
 * re-check stays as a backstop. Sign-in / reset screens and the unlock
 * screen itself are never blocked, so it cannot loop.
 *  - canDeactivate on the unlock screen: leaving it for an app screen is
 *    refused outright. Deactivate guards run before any activate guard, so
 *    the target's own guards (and their can-access requests) never start;
 *    the router puts the browser URL back on the unlock screen.
 *  - canActivate first on every top-level app route, canActivateChild on
 *    every CMS shell (navigations inside an active shell).
 */
@Injectable({providedIn: 'root'})
export class ReopenLockGuard implements CanActivate, CanActivateChild, CanDeactivate<any> {
  constructor(private router: Router, private idleLock: IdleLockService) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    const returnUrl = unlockNavigationFor(state && state.url);
    if (returnUrl === null || !this.idleLock.mustLockBeforeActivation(Date.now())) {
      return true;
    }
    const target = this.router.createUrlTree([UNLOCK_ROUTE], {queryParams: {returnUrl}});
    // Already showing exactly that unlock screen: the router ignores a redirect
    // to the current URL and would leave the target URL in the address bar.
    // Refusing instead puts the address bar back.
    return this.router.url === this.router.serializeUrl(target) ? false : target;
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.canActivate(childRoute, state);
  }

  canDeactivate(component: any, currentRoute: ActivatedRouteSnapshot, currentState: RouterStateSnapshot,
                nextState?: RouterStateSnapshot): boolean {
    if (!nextState || unlockNavigationFor(nextState.url) === null) {
      return true;
    }
    return !this.idleLock.mustLockBeforeActivation(Date.now());
  }
}
